import {
  AttachmentPreview,
  BoxV2 as Box,
  MultimediaList,
  MultimediaUploader,
} from 'portal-commons';
import { FunctionComponent, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { faFileImage } from '@fortawesome/pro-regular-svg-icons';
import { Button, Grid, makeStyles } from '@material-ui/core';

import HeadingBlock from './HeadingBlock';
import DeleteSampleMultimediaModal from '../DeleteSampleMultimediaModal';
import MaskedContainer from '../MaskedContainer';
import UploadSizeExceededModal from '../UploadSizeExceededModal';
import { deleteAttachment, uploadAttachment } from '../../apis';
import { downloadAttachmentToDesktop } from '../../apiServices';
import { MAX_FILE_SIZE, MAX_SAMPLE_MULTIMEDIA_COUNT } from '../../constants';
import { CampaignDetailsFormValues, FileFormats } from '../../types';
import { isFieldGreyOut } from '../../utils';
import { AttachmentFolder } from '@/utils/types';

interface SampleMultimediaSectionProps {
  greyOutFields: string[];
  supportedFileFormats: FileFormats;
}

const useStyles = makeStyles({
  button: {
    background: '#D41C54',
    boxShadow: 'none',
    textTransform: 'none',
    '&:hover': {
      background: '#D41C54',
    },
  },
});

const SampleMultimediaSection: FunctionComponent<
  SampleMultimediaSectionProps
> = ({ greyOutFields, supportedFileFormats }) => {
  const { control } = useFormContext<CampaignDetailsFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sampleMultimedia',
  });
  const classes = useStyles();
  const [file, setFile] = useState<File>();
  const [uploadSizeExceededOpen, setUploadSizeExceededOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [uploading, setUploading] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(-1);

  const handleFileChange = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setUploadSizeExceededOpen(true);
      setFile(undefined);
    } else {
      setFile(file);
    }
  };

  const handleDownload = async (index: number) => {
    await downloadAttachmentToDesktop(
      fields[index].uuid,
      fields[index].fileName
    );
  };

  const handleDelete = async () => {
    const attachment = fields[deleteIndex];
    const response = await deleteAttachment(attachment.uuid);
    if (response) {
      remove(deleteIndex);
    }
    setDeleteIndex(-1);
  };

  const handleUpload = async () => {
    if (file && fields.length < MAX_SAMPLE_MULTIMEDIA_COUNT) {
      setUploading(true);
      const attachment = await uploadAttachment(
        AttachmentFolder.SampleMultimedia,
        file
      );
      if (attachment) {
        append({
          ...attachment,
          file,
        });
      }
      setUploading(false);
      setFile(undefined);
    }
  };

  return (
    <Grid item xs={12} data-testid="campaignDetailsFormSampleMultimedia">
      <MaskedContainer
        className="sample-multimedia wrapper"
        masked={isFieldGreyOut(greyOutFields, 'mmsMediaUuid')}
      >
        <HeadingBlock icon={faFileImage} title="Sample Multimedia">
          <Box
            sx={{
              marginTop: '8px',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '16px',
            }}
          >
            Provides an area to upload sample MMS content. Not intended for
            call-to-action, terms and conditions, or privacy policy information.
          </Box>
        </HeadingBlock>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <MultimediaUploader
              disabled={fields.length >= MAX_SAMPLE_MULTIMEDIA_COUNT}
              disabledText={`Max ${MAX_SAMPLE_MULTIMEDIA_COUNT} uploads per campaign is allowed`}
              supportedMimeTypes={supportedFileFormats}
              onChange={handleFileChange}
            />
          </Grid>
          <Grid item xs={6}>
            <MultimediaList
              data-testid="campaignDetailSampleMultimediaFiles"
              title="Sample Multimedia Files"
              editable
              loading={activeIndex > -1 && !fields[activeIndex].file}
              attachments={fields}
              onSelect={setActiveIndex}
              onDelete={setDeleteIndex}
              onDownload={handleDownload}
            />
          </Grid>
          {fields.length < 5 && (
            <Grid item xs={12}>
              <div className="text-center">
                <Button
                  classes={{
                    root: classes.button,
                  }}
                  disabled={!file || uploading}
                  variant="contained"
                  color="primary"
                  data-testid="campaignDetailsFormSampleMultimediaAddButton"
                  onClick={handleUpload}
                >
                  {uploading ? 'Processing...' : '+ Add Sample Multimedia'}
                </Button>
              </div>
            </Grid>
          )}
          {activeIndex > -1 && !!fields[activeIndex].file && (
            <AttachmentPreview
              onClose={() => setActiveIndex(-1)}
              file={fields[activeIndex].file}
              mimeType={fields[activeIndex].mimeType}
              data-testid="campaignDetailsFormSampleMultimediaBackdrop"
              style={{ zIndex: 5 }}
            />
          )}
        </Grid>
      </MaskedContainer>
      <UploadSizeExceededModal
        open={uploadSizeExceededOpen}
        onClose={() => {
          setUploadSizeExceededOpen(false);
        }}
      />
      <DeleteSampleMultimediaModal
        open={!!fields[deleteIndex]}
        fileName={fields[deleteIndex]?.fileName}
        onCancel={() => setDeleteIndex(-1)}
        onSubmit={handleDelete}
      />
    </Grid>
  );
};

export default SampleMultimediaSection;
